import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useUpdateCalculationSchema } from '../../../queries/use-update-custom-worksheet-schema';
import { CWState, loadSchema, markClean } from './store/slice';
import { toastNotification } from '../../../components/toast/Toast';
import { selectIsDirty, selectSchema } from './store/selectors';

import queryClient from '../../../query-client';
import { Calculation } from '../../../types/worksheet';

type SchemaPersistanceProps = {
    worksheetId: string;
    calculation: Calculation;
};

function SchemaPersistance({ worksheetId, calculation }: SchemaPersistanceProps): null {
    const dispatch = useDispatch();
    const firstUpdate = useRef(true);

    const { mutate } = useUpdateCalculationSchema(calculation.id);

    const timeout = useRef<NodeJS.Timeout | null>(null);

    const schema = useSelector((state: { schema: CWState }) => selectSchema({ state }));
    const isDirty = useSelector(selectIsDirty);

    /**
     * Every time the schema is updated, send the schema to the backend for persistance and review condition calculations
     */
    useEffect(() => {
        // Don't send an update on first render
        if (firstUpdate.current === true) {
            firstUpdate.current = false;
            return;
        }

        if (timeout.current !== null) {
            clearTimeout(timeout.current);
        }

        // Clear any previous mutations
        timeout.current = setTimeout(() => {
            if (isDirty) {
                dispatch(markClean());

                mutate(
                    { schema },
                    {
                        onError: (err) => {
                            toastNotification('error', 'Unable to save your changes', err.response?.data.message);
                            dispatch(loadSchema({ schema: calculation.schema }));
                        },
                        onSettled: () => {
                            queryClient.invalidateQueries(['worksheets', worksheetId]);
                        },
                    }
                );
            }
        }, 500);

        return (): void => {
            if (timeout.current !== null) clearTimeout(timeout.current);
        };
    }, [schema, mutate, isDirty, dispatch, worksheetId, calculation.schema]);

    return null;
}

export default SchemaPersistance;
